import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

// Timeout for auth callback processing
const CALLBACK_TIMEOUT_MS = 15000;

export const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUserDirectly } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Processing...');

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      console.log('[AuthCallback] Processing auth callback...');
      setStatus('Checking authentication...');
      
      // Get session_id from hash
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');

      console.log('[AuthCallback] Session ID:', sessionId ? 'present' : 'missing');

      if (!sessionId) {
        console.log('[AuthCallback] No session_id in URL, redirecting to login');
        setError('No authentication token found');
        setTimeout(() => navigate('/login', { replace: true }), 1500);
        return;
      }

      // Set timeout fallback
      const timeoutId = setTimeout(() => {
        console.log('[AuthCallback] Timeout waiting for session exchange');
        setError('Authentication timed out');
        setTimeout(() => navigate('/login', { replace: true }), 1500);
      }, CALLBACK_TIMEOUT_MS);

      try {
        setStatus('Exchanging session token...');
        console.log('[AuthCallback] Exchanging session_id for session token...');
        
        // Exchange session_id for session token
        // The backend will set the session cookie
        const response = await api.post(
          '/auth/session',
          { session_id: sessionId },
          { timeout: 10000 }
        );

        clearTimeout(timeoutId);
        console.log('[AuthCallback] Session exchanged successfully');
        setStatus('Session established!');

        // Clear the hash from URL immediately
        window.history.replaceState(null, '', '/dashboard');
        console.log('[AuthCallback] URL hash cleared');

        // Set user state directly from the response (backend returns user data)
        if (response.data) {
          console.log('[AuthCallback] Setting user:', response.data.email);
          setUserDirectly(response.data);
          
          // Store domain for future cache clearing
          localStorage.setItem('ss_auth_domain', window.location.hostname);
        }

        // Small delay to ensure cookie is set before navigation
        await new Promise(resolve => setTimeout(resolve, 200));

        // Navigate to dashboard with replace to prevent back button issues
        console.log('[AuthCallback] Navigating to dashboard');
        navigate('/dashboard', { replace: true });
      } catch (error) {
        clearTimeout(timeoutId);
        console.error('[AuthCallback] Auth callback error:', error);
        
        const errorMsg = error.response?.data?.detail || error.message || 'Authentication failed';
        setError(errorMsg);
        
        // Clear any partial auth state
        localStorage.removeItem('ss_user');
        
        setTimeout(() => {
          console.log('[AuthCallback] Redirecting to login after error');
          navigate('/login', { replace: true });
        }, 2000);
      }
    };

    processAuth();
  }, [navigate, setUserDirectly]);

  return (
    <div className="min-h-screen bg-ss-bg flex items-center justify-center">
      <div className="text-center">
        {error ? (
          <>
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <span className="text-red-500 text-xl">!</span>
            </div>
            <p className="text-red-400 mb-2">Authentication Error</p>
            <p className="text-ss-muted text-sm">{error}</p>
            <p className="text-ss-muted text-xs mt-2">Redirecting to login...</p>
          </>
        ) : (
          <>
            <div className="w-12 h-12 border-4 border-ss-cyan border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-ss-muted">{status}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
